import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AutomationCoverageChart = ({ automationCoverage, projectId }) => {
  const navigate = useNavigate();

  if (!automationCoverage) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Automation Coverage</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No automation data available
        </div>
      </div>
    );
  }

  const {
    automated_integration = 0,
    manual_with_upload = 0,
    manual_self_report = 0,
  } = automationCoverage;

  const totalControls = automated_integration + manual_with_upload + manual_self_report;

  // Transform data for PieChart
  const data = [
    { name: 'Auto-Verified', value: automated_integration, color: '#22c55e' },
    { name: 'Manual Upload', value: manual_with_upload, color: '#3b82f6' },
    { name: 'Self-Reported', value: manual_self_report, color: '#f59e0b' },
  ].filter((item) => item.value > 0); // Only show non-zero values

  // Calculate percentage of automated controls
  const automatedPercentage = totalControls > 0 ? (automated_integration / totalControls) * 100 : 0;
  const showSuggestion = automatedPercentage < 40;

  // Custom label for center of donut chart
  const renderCenterLabel = () => (
    <text
      x="50%"
      y="50%"
      textAnchor="middle"
      dominantBaseline="middle"
      className="text-2xl font-bold"
      fill="#111827"
    >
      {totalControls}
    </text>
  );

  if (totalControls === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Automation Coverage</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No controls to display
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Automation Coverage</h3>

      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
            label={(entry) => `${entry.name}: ${entry.value}`}
            labelLine={false}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              color: '#111827',
            }}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value) => <span style={{ color: '#374151' }}>{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Summary text */}
      <div className="mt-4 text-center text-sm text-gray-600">
        <p>
          <span className="font-semibold text-green-700">{automated_integration}</span> of{' '}
          <span className="font-semibold">{totalControls}</span> controls are automatically verified.{' '}
          <span className="font-semibold text-blue-700">
            {manual_with_upload + manual_self_report}
          </span>{' '}
          controls require manual evidence.
        </p>
      </div>

      {/* Suggestion if automation coverage is low */}
      {showSuggestion && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-800">
                <span className="font-medium">Suggestion:</span> Connect more integrations to increase
                automated coverage.
              </p>
              <button
                onClick={() => navigate(`/compliance/projects/${projectId}/integrations`)}
                className="mt-2 text-sm text-amber-700 hover:text-amber-900 font-medium underline"
              >
                View Integrations
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationCoverageChart;
